import { useGetTripSummaryQuery } from "@/redux/services/allocationApi";
import {
	Alert,
	Button,
	Card,
	Col,
	Descriptions,
	Empty,
	Flex,
	Result,
	Row,
	Spin,
	Tag,
	Timeline,
	Typography,
} from "antd";
import {
	CarOutlined,
	ClockCircleOutlined,
	EnvironmentOutlined,
	LinkOutlined,
	MessageOutlined,
	PhoneOutlined,
	UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import "./style.css";

const { Title, Text } = Typography;
const dateFormat = "YYYY-MM-DD";

const formatValue = (value, fallback = "-") => {
	if (value === null || value === undefined || value === "") {
		return fallback;
	}
	return value;
};

const normalizeOrderSubId = (value) => {
	if (value === null || value === undefined || value === "") {
		return "";
	}
	return String(value).trim();
};

const getPickupAddress = (item) => {
	const address = formatValue(item.pickup_address, "");
	const location = formatValue(item.pickup_location, "");

	if (address) {
		return `${address}, Australia`;
	}
	if (location) {
		return `${location}, Melbourne VIC, Australia`;
	}
	return "";
};

const sortByPickupTime = (items = []) => {
	return [...items].sort((a, b) =>
		formatValue(a.pickup_time, "").localeCompare(formatValue(b.pickup_time, ""))
	);
};

const groupPickupStops = (items = []) => {
	const groupedStops = {};

	sortByPickupTime(items).forEach((item) => {
		const key = [
			formatValue(item.pickup_time, ""),
			formatValue(item.pickup_location, ""),
			formatValue(item.pickup_address, ""),
		].join("|");

		if (!groupedStops[key]) {
			groupedStops[key] = {
				pickup_time: item.pickup_time,
				pickup_location: item.pickup_location,
				pickup_address: item.pickup_address,
				map_address: getPickupAddress(item),
				clients: [],
			};
		}

		groupedStops[key].clients.push(item);
	});

	return Object.values(groupedStops);
};

const getRoutePoints = (stops = []) => {
	const seen = new Set();

	return stops
		.map((stop) => stop.map_address)
		.filter(Boolean)
		.filter((point) => {
			const key = point.toLowerCase();
			if (seen.has(key)) {
				return false;
			}
			seen.add(key);
			return true;
		});
};

const buildMapSrc = (stops = []) => {
	const points = getRoutePoints(stops);

	if (points.length === 0) {
		return "";
	}

	if (points.length === 1) {
		return `https://maps.google.com/maps?q=${encodeURIComponent(
			points[0]
		)}&output=embed`;
	}

	const origin = points[0];
	const destinations = points.slice(1).map(encodeURIComponent).join("+to:");

	return `https://maps.google.com/maps?saddr=${encodeURIComponent(
		origin
	)}&daddr=${destinations}&output=embed`;
};

const buildMapsLink = (stops = []) => {
	const points = getRoutePoints(stops);

	if (points.length === 0) {
		return "";
	}

	if (points.length === 1) {
		return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
			points[0]
		)}`;
	}

	const origin = points[0];
	const destination = points[points.length - 1];
	const waypoints = points.slice(1, -1);

	const params = new URLSearchParams({
		api: "1",
		origin,
		destination,
		travelmode: "driving",
	});

	if (waypoints.length > 0) {
		params.set("waypoints", waypoints.join("|"));
	}

	return `https://www.google.com/maps/dir/?${params.toString()}`;
};

const findTripDetail = (productList = [], orderSubId) => {
	if (!orderSubId) {
		return productList;
	}

	const matchedProducts = [];

	productList.forEach((product) => {
		const matchedAllocations = product.allocation_list
			.map((allocation) => {
				const hasTargetOrder = allocation.items.some(
					(item) =>
						item.order_sub_id !== null &&
						item.order_sub_id !== undefined &&
						normalizeOrderSubId(item.order_sub_id) === orderSubId
				);

				return hasTargetOrder
					? {
						...allocation,
						target_order_sub_id: orderSubId,
					}
					: null;
			})
			.filter(Boolean);

		if (matchedAllocations.length > 0) {
			matchedProducts.push({
				...product,
				allocation_list: matchedAllocations,
			});
		}
	});

	return matchedProducts;
};

const getVehicleTitle = (allocation) => {
	const plate = allocation.vehicle?.vehicle_plate_number;

	return [plate].filter(Boolean).join(" - ") || "未分配车辆";
};

const getPhoneHrefValue = (phone) => {
	const value = String(formatValue(phone, "")).replace(/[\s()-]/g, "");

	if (!value) {
		return "";
	}

	if (value.startsWith("+")) {
		return value;
	}

	if (value.startsWith("00")) {
		return `+${value.slice(2)}`;
	}

	if (!value.startsWith("0")) {
		return `+${value}`;
	}

	return value;
};

function PhoneActionLinks({ phone, className }) {
	const phoneHrefValue = getPhoneHrefValue(phone);

	if (!phoneHrefValue) {
		return <Text type="secondary">-</Text>;
	}

	return (
		<Flex className={className} align="center" gap={6} wrap="wrap">
			<Text>{formatValue(phone)}</Text>
			<Button
				href={`tel:${phoneHrefValue}`}
				icon={<PhoneOutlined />}
				size="small"
				type="link"
			>
				打电话
			</Button>
			<Button
				href={`sms:${phoneHrefValue}`}
				icon={<MessageOutlined />}
				size="small"
				type="link"
			>
				发短信
			</Button>
		</Flex>
	);
}

function RouteMap({ stops, title }) {
	const mapSrc = buildMapSrc(stops);
	const mapsLink = buildMapsLink(stops);

	if (!mapSrc) {
		return (
			<div className="trip-detail-map-empty">
				<Empty description="暂无可用于地图的上车地址" />
			</div>
		);
	}

	return (
		<div className="trip-detail-map-wrap">
			<iframe
				title={title}
				src={mapSrc}
				className="trip-detail-map"
				loading="lazy"
				referrerPolicy="no-referrer-when-downgrade"
			/>
			<Button
				className="trip-detail-map-link"
				icon={<LinkOutlined />}
				href={mapsLink}
				target="_blank"
				rel="noreferrer"
			>
				打开 Google Maps
			</Button>
		</div>
	);
}

function VehicleDetail({ allocation, product, productColor }) {
	const stops = useMemo(
		() => groupPickupStops(allocation.items),
		[allocation.items]
	);
	const sortedItems = useMemo(
		() => sortByPickupTime(allocation.items),
		[allocation.items]
	);
	const targetOrderSubId = allocation.target_order_sub_id;
	const passengerItems = useMemo(() => {
		if (!targetOrderSubId) {
			return sortedItems;
		}

		return sortedItems.filter(
			(item) => normalizeOrderSubId(item.order_sub_id) === targetOrderSubId
		);
	}, [sortedItems, targetOrderSubId]);
	const passengerItem = passengerItems[0] || {};

	const vehicle = allocation.vehicle || {};
	const driver = allocation.driver || {};

	const timelineItems = stops.map((stop) => {
		const isTargetStop = stop.clients.some(
			(client) => normalizeOrderSubId(client.order_sub_id) === targetOrderSubId
		);

		return {
			dot: (
				<ClockCircleOutlined
					className={isTargetStop ? "trip-detail-target-dot" : ""}
				/>
			),
			color: isTargetStop ? "green" : "gray",
			children: (
				<div
					className={
						isTargetStop
							? "trip-detail-stop trip-detail-target-stop"
							: "trip-detail-stop trip-detail-disabled-stop"
					}
				>
					{isTargetStop && (
						<div className="trip-detail-stop-badge">
							<Tag color="green">您的上车点</Tag>
						</div>
					)}
					<div className="trip-detail-stop-head">
						<Tag color={isTargetStop ? "green" : "default"}>
							{formatValue(stop.pickup_time)}
						</Tag>
						<Text strong={isTargetStop}>
							{formatValue(
								stop.pickup_address || stop.pickup_location
							)}
						</Text>
					</div>
					{stop.pickup_address && (
						<Text type="secondary">
							{formatValue(stop.pickup_location)}
						</Text>
					)}
				</div>
			),
		};
	});

	return (
		<Card
			className="trip-detail-vehicle-card"
			title={
				<Flex align="center" gap={10} wrap="wrap">
					<CarOutlined />
					<span>{getVehicleTitle(allocation)}</span>
					<Tag color={productColor}>{product.product_title}</Tag>
				</Flex>
			}
		>
			<Row gutter={[20, 20]}>
				<Col span={24}>
					<Alert
						className="trip-detail-note"
						type="info"
						showIcon
						message="墨尔本散拼参团须知与注意事项："
						description={
							<div>
								<p>
									1. 请您提前5分钟到达上车点等候，如集合时间未到，将视为自愿放弃行程；
								</p>
								<p>
									2. 如遇路况等因素车辆到达时间有所延误，司导将提前与您沟通；
								</p>
								<p>
									3. 请提前准备$5/人小费(现金)，当日支付；
								</p>
								<p>
									4. 如有晕车情况，请提前自备晕车药和呕吐袋；
								</p>
								<p>
									5.为保证乘客安全以及维持车内环境，请勿在车内食用任何食品及饮料；如因私人原因而严重影响车内清洁，需支付清洁费用（$200起）；
								</p>
								<p>
									看到发车信息并准时上车默认为同意以上条款
								</p>
								<p>
									再次感谢您选择我们，祝您旅途愉快！
								</p>
							</div>
						}
					/>
				</Col>
				<Col span={24}>
					<div className="trip-detail-passenger-card">
						<Title level={4} className="trip-section-title">
							{formatValue(passengerItem.client_name)}
						</Title>
						<div className="trip-detail-passenger-grid">
							<div className="trip-detail-passenger-item">
								<Text type="secondary">上车时间</Text>
								<div className="trip-detail-passenger-value">
									<ClockCircleOutlined />
									<Tag color="blue">{formatValue(passengerItem.pickup_time)}</Tag>
								</div>
							</div>
							<div className="trip-detail-passenger-item trip-detail-passenger-location">
								<Text type="secondary">上车地点</Text>
								<div className="trip-detail-passenger-value">
									<EnvironmentOutlined />
									<Flex vertical gap={2}>
										<Text strong>
											{formatValue(
												passengerItem.pickup_address ||
													passengerItem.pickup_location
											)}
										</Text>
										{passengerItem.pickup_address && (
											<Text type="secondary">
												{formatValue(passengerItem.pickup_location)}
											</Text>
										)}
									</Flex>
								</div>
							</div>
						</div>
					</div>
				</Col>
				<Col xs={24} xl={10}>
					<Title level={4} className="trip-section-title">
						您的车辆信息
					</Title>

					<Descriptions column={1} size="small" bordered>
						<Descriptions.Item label="车牌">
							{formatValue(vehicle.vehicle_plate_number)}
						</Descriptions.Item>
						<Descriptions.Item label="司机">
							<UserOutlined /> {formatValue(driver.driver_name)}
						</Descriptions.Item>
						<Descriptions.Item label="电话">
							<PhoneActionLinks
								className="trip-detail-phone-actions"
								phone={driver.driver_phone}
							/>
						</Descriptions.Item>
					</Descriptions>

					<div className="trip-detail-timeline">
						<Title level={4} className="trip-section-title">
							车辆接送路径
						</Title>
						<Timeline items={timelineItems} />
						<Text className="trip-detail-route-tip" type="secondary">
							请在您选择的上车地点【{formatValue(passengerItem.pickup_location)}】上车，发车路径与时间仅供参考，公司有权根据实际当天情况做调整，调整将不会更新此页面，如需修改上车点请提前24小时联系客服，未联系客服擅自到达任何地点有可能错过车辆而无法上车，错过上车时间司机将无法返回。感谢理解与配合！
						</Text>
					</div>
				</Col>
				<Col xs={24} xl={14}>
					<RouteMap
						stops={stops}
						title={`${getVehicleTitle(allocation)} route map`}
					/>
				</Col>
			</Row>
		</Card>
	);
}

function TripDetailPage() {
	const params = useParams();
	const [searchParams] = useSearchParams();
	const { data, isError, isLoading } = useGetTripSummaryQuery();
	const orderSubId = normalizeOrderSubId(
		params.orderSubId || searchParams.get("order_sub_id")
	);

	const products = useMemo(() => {
		if (!data?.product_list) {
			return [];
		}

		return findTripDetail(data.product_list, orderSubId).map((product) => ({
			...product,
			product_time: data.travel_date,
		}));
	}, [data, orderSubId]);

	if (!orderSubId) {
		return (
			<Result
				status="404"
				title="404"
				subTitle="没有找到对应 Order ID 的行程信息"
			/>
		);
	}

	const selectedDate = data?.travel_date
		? dayjs(data.travel_date).format(dateFormat)
		: "";

	return (
		<div className="trip-detail-page">
			<Flex
				className="trip-detail-header"
				justify="space-between"
				align="center"
				wrap="wrap"
				gap={12}
			>
				<div>
					<Title level={2} className="trip-detail-title">
						{selectedDate} 行程详情
					</Title>
					<Text type="secondary">
						{orderSubId ? ` ${orderSubId}` : " · 全部车辆"}
					</Text>
				</div>
				<Flex gap={8} wrap="wrap">
				</Flex>
			</Flex>

			{isError && (
				<Alert
					message="Error"
					description="Something went wrong ..."
					type="error"
					showIcon
				/>
			)}

			{isLoading && (
				<Flex justify="center" align="center" className="trip-detail-loading">
					<Spin size="large" />
				</Flex>
			)}

			{!isLoading && products.length === 0 && (
				<Result
					status="404"
					title="No Data"
					subTitle={
						orderSubId
							? "没有找到对应 Order ID 的行程信息"
							: "当前日期暂无行程信息"
					}
				/>
			)}

			{products.map((product) => (
				<div key={product.product_id} className="trip-detail-product">
					{product.allocation_list.map((allocation) => (
						<VehicleDetail
							key={allocation.list_name || allocation.vehicle?.vehicle_plate_number}
							allocation={allocation}
							product={product}
							productColor={product.product_color}
						/>
					))}
				</div>
			))}
		</div>
	);
}

export default TripDetailPage;
